import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Star, TrendingUp, Users, Loader2, AlertCircle } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import leaderboardService from '../../services/leaderboardService';

// Sample leaderboard data (fallback)
const MOCK_LEADERBOARD_DATA = [
  { id: 1, name: 'Wanjiku M.', avatar: 'girl', points: 2450, streak: 15, rank: 1 },
  { id: 2, name: 'Ochieng K.', avatar: 'boy', points: 2320, streak: 12, rank: 2 },
  { id: 3, name: 'Amina H.', avatar: 'girl', points: 2180, streak: 10, rank: 3 },
  { id: 4, name: 'Kamau J.', avatar: 'boy', points: 1950, streak: 8, rank: 4 },
  { id: 5, name: 'Fatima A.', avatar: 'girl', points: 1820, streak: 7, rank: 5 },
  { id: 6, name: 'Brian M.', avatar: 'boy', points: 1650, streak: 5, rank: 6 },
  { id: 7, name: 'Sarah N.', avatar: 'girl', points: 1540, streak: 6, rank: 7 },
  { id: 8, name: 'David O.', avatar: 'boy', points: 1420, streak: 4, rank: 8 },
];

const TABS = [
  { id: 'class', label: 'Class', icon: Users },
  { id: 'school', label: 'School', icon: Medal },
  { id: 'global', label: 'Global', icon: Trophy },
];

export default function Leaderboard() {
  const { student, isAuthenticated } = useAuthStore();
  const [activeTab, setActiveTab] = useState('class');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Fetch leaderboard data
  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (!isAuthenticated || !student) {
        // Use mock data if not authenticated
        setLeaderboardData(MOCK_LEADERBOARD_DATA);
        setCurrentUserId(4);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch leaderboard based on active tab (use student endpoints)
        let data;
        if (activeTab === 'class') {
          data = await leaderboardService.getMyClassLeaderboard(10);
        } else if (activeTab === 'school') {
          data = await leaderboardService.getMySchoolLeaderboard(10);
        } else {
          data = await leaderboardService.getMyGlobalLeaderboard(10);
        }

        // Transform API data to component format
        const transformed = (data.entries || []).map((entry) => ({
          id: entry.student_id,
          name: entry.student_name,
          avatar: entry.avatar || 'seedling',
          points: entry.total_points,
          streak: entry.current_week || 0,
          rank: entry.rank,
          isCurrentUser: entry.is_current_user,
        }));

        setLeaderboardData(transformed);
        setCurrentUserId(student.id);

        // Also fetch my rank
        try {
          const rankData = await leaderboardService.getMyRank();
          setMyRank(rankData);
        } catch (rankErr) {
          console.error('Failed to fetch rank:', rankErr);
        }
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err);
        setError('Could not load leaderboard. Using sample data.');
        setLeaderboardData(MOCK_LEADERBOARD_DATA);
        setCurrentUserId(4);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [isAuthenticated, student, activeTab]);

  const getRankBadge = (rank) => {
    switch (rank) {
      case 1:
        return (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 flex items-center justify-center shadow-lg">
            <Trophy size={16} className="text-yellow-800" />
          </div>
        );
      case 2:
        return (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow">
            <Medal size={16} className="text-gray-600" />
          </div>
        );
      case 3:
        return (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-300 to-orange-500 flex items-center justify-center shadow">
            <Medal size={16} className="text-orange-800" />
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <span className="text-gray-600 font-bold text-sm">{rank}</span>
          </div>
        );
    }
  };

  const getAvatarEmoji = (avatar) => {
    const avatars = {
      seedling: '🌱',
      sprout: '🌿',
      flower: '🌸',
      tree: '🌳',
      boy: '👦',
      girl: '👧',
    };
    return avatars[avatar] || '🌱';
  };

  // Get current user's rank for the active tab
  const getCurrentRank = () => {
    if (myRank) {
      if (activeTab === 'class') return myRank.class_rank;
      if (activeTab === 'school') return myRank.school_rank;
      return myRank.global_rank;
    }
    // Fallback to finding in leaderboard
    const user = leaderboardData.find((p) => p.id === currentUserId || p.isCurrentUser);
    return user?.rank || 4;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-leaf-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  // Ensure we have at least 3 entries for podium
  const podiumData = leaderboardData.slice(0, 3);
  const listData = leaderboardData.slice(3);

  return (
    <div className="pb-4">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl mb-2">Leaderboard</h1>
        <p className="text-gray-600">See how you rank among green champions!</p>
      </div>

      {/* Error Banner (non-blocking) */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-2"
        >
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-amber-700 text-sm">{error}</p>
        </motion.div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl overflow-hidden">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-lg
                         font-medium transition-all duration-200 whitespace-nowrap ${
                           activeTab === tab.id
                             ? 'bg-white text-leaf-700 shadow'
                             : 'text-gray-500 hover:text-gray-700'
                         }`}
            >
              <Icon size={16} className="flex-shrink-0" />
              <span className="text-sm truncate">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Top 3 Podium */}
      {podiumData.length >= 3 && (
        <div className="flex justify-center items-end gap-4 mb-8 h-40">
          {/* 2nd Place */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center"
          >
            <div className="w-16 h-16 rounded-full bg-gray-200 mx-auto mb-2 flex items-center justify-center">
              <span className="text-2xl">{getAvatarEmoji(podiumData[1]?.avatar)}</span>
            </div>
            <p className="font-medium text-sm truncate w-20">{podiumData[1]?.name}</p>
            <p className="text-xs text-gray-500">{podiumData[1]?.points} pts</p>
            <div className="w-16 h-20 bg-gradient-to-t from-gray-300 to-gray-200 rounded-t-lg mt-2 flex items-center justify-center">
              <span className="text-2xl font-bold text-gray-600">2</span>
            </div>
          </motion.div>

          {/* 1st Place */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-20 h-20 rounded-full bg-sunshine-200 mx-auto mb-2 flex items-center justify-center ring-4 ring-sunshine-300">
              <span className="text-3xl">{getAvatarEmoji(podiumData[0]?.avatar)}</span>
            </div>
            <p className="font-bold truncate w-24">{podiumData[0]?.name}</p>
            <p className="text-sm text-sunshine-600 font-semibold">{podiumData[0]?.points} pts</p>
            <div className="w-20 h-28 bg-gradient-to-t from-sunshine-400 to-sunshine-300 rounded-t-lg mt-2 flex items-center justify-center">
              <span className="text-3xl font-bold text-sunshine-700">1</span>
            </div>
          </motion.div>

          {/* 3rd Place */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center"
          >
            <div className="w-16 h-16 rounded-full bg-orange-100 mx-auto mb-2 flex items-center justify-center">
              <span className="text-2xl">{getAvatarEmoji(podiumData[2]?.avatar)}</span>
            </div>
            <p className="font-medium text-sm truncate w-20">{podiumData[2]?.name}</p>
            <p className="text-xs text-gray-500">{podiumData[2]?.points} pts</p>
            <div className="w-16 h-16 bg-gradient-to-t from-orange-300 to-orange-200 rounded-t-lg mt-2 flex items-center justify-center">
              <span className="text-2xl font-bold text-orange-700">3</span>
            </div>
          </motion.div>
        </div>
      )}

      {/* Full List */}
      <div className="space-y-3">
        {listData.map((player, index) => (
          <motion.div
            key={player.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + index * 0.05 }}
            className={`card p-4 flex items-center gap-4 ${
              player.id === currentUserId || player.isCurrentUser
                ? 'ring-2 ring-leaf-500 bg-leaf-50'
                : ''
            }`}
          >
            {/* Rank */}
            {getRankBadge(player.rank)}

            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-leaf-100 flex items-center justify-center">
              <span className="text-lg">{getAvatarEmoji(player.avatar)}</span>
            </div>

            {/* Info */}
            <div className="flex-1">
              <p className="font-medium">
                {player.name}
                {(player.id === currentUserId || player.isCurrentUser) && (
                  <span className="text-leaf-600 text-sm ml-2">(You)</span>
                )}
              </p>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Star size={14} className="text-sunshine-500" />
                  {player.points}
                </span>
                <span className="flex items-center gap-1">
                  <TrendingUp size={14} className="text-orange-500" />
                  Week {player.streak}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty state */}
      {leaderboardData.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No students on the leaderboard yet.</p>
          <p className="text-sm text-gray-400 mt-1">Complete activities to start earning points!</p>
        </div>
      )}

      {/* Your Position */}
      <div className="mt-6 p-4 bg-leaf-100 rounded-xl text-center">
        <p className="text-sm text-leaf-700">
          You are ranked <span className="font-bold">#{getCurrentRank()}</span> in your {activeTab}.
          <br />
          Keep completing activities to climb higher!
        </p>
      </div>
    </div>
  );
}
